// Resolução do idioma em três níveis: parceiro → proprietário → usuário.
//
// O mais específico vence. O molde é o `userTheme` (App.tsx): GET /user/:id no
// login, PUT parcial ao escolher. A diferença é que tema tem UMA fonte e
// idioma tem três, então o valor efetivo é calculado, não lido.
//
// Enquanto o backend não devolver os campos, tudo cai em `detected` — que é o
// comportamento de hoje (detector do i18next lendo localStorage/navegador).
import api from '../services/api';
import i18n, { supportedLanguages } from './index';

export type NivelIdioma = 'user' | 'tenant' | 'partner' | 'detected';

export interface IdiomaResolvido {
  /** Código suportado, ou null quando nenhum nível definiu idioma. */
  code: string | null;
  source: NivelIdioma;
}

/**
 * Converte o que vier do servidor num código que o app realmente tem.
 *
 * Aceita o código exato ('pt-PT') e também só o idioma ('pt-BR' → 'pt'),
 * porque o backend pode gravar um BCP-47 completo.
 */
export function normalizarIdioma(bruto: unknown): string | null {
  if (typeof bruto !== 'string') return null;
  const valor = bruto.trim();
  if (!valor) return null;

  const exato = supportedLanguages.find(
    (l) => l.code.toLowerCase() === valor.toLowerCase(),
  );
  if (exato) return exato.code;

  const base = valor.split('-')[0].toLowerCase();
  const porIdioma = supportedLanguages.find(
    (l) => l.code.split('-')[0].toLowerCase() === base,
  );
  return porIdioma ? porIdioma.code : null;
}

/** Último resultado da resolução — o seletor usa para dizer de onde veio. */
let ultimaOrigem: NivelIdioma = 'detected';
export const origemDoIdioma = (): NivelIdioma => ultimaOrigem;

const lerCampo = (resp: any, envelope: string, campo: string): unknown => {
  const data = resp?.data?.[envelope] ?? resp?.data ?? {};
  return data?.[campo];
};

/**
 * Resolve o idioma consultando o servidor, do mais específico ao mais genérico.
 *
 * A busca é preguiçosa de propósito: se o usuário já tem idioma próprio, nem
 * chega a pedir proprietário e parceiro. Só quem não definiu nada paga as três
 * chamadas — e esse é o caso raro.
 */
export async function resolverIdioma(userId: number): Promise<IdiomaResolvido> {
  let tenantId: unknown;
  let partnerId: unknown;

  // Atalho: o login já devolve o idioma do usuário. Se ele tem um, não há o
  // que resolver — e a ida ao servidor só atrasaria o que já está decidido.
  try {
    const guardado = JSON.parse(localStorage.getItem('user') || 'null');
    const doLogin = normalizarIdioma(guardado?.userLanguage);
    if (doLogin) {
      ultimaOrigem = 'user';
      return { code: doLogin, source: 'user' };
    }
  } catch {
    /* sem registro local — segue pelo servidor */
  }

  try {
    const resp = await api.get(`/user/${userId}`);
    const data = resp?.data?.user ?? resp?.data ?? {};

    const doUsuario = normalizarIdioma(data.userLanguage);
    if (doUsuario) {
      ultimaOrigem = 'user';
      return { code: doUsuario, source: 'user' };
    }

    tenantId = data.userTenant;
    // No USUÁRIO o campo é `userPartner`. `partnerId` é do próprio parceiro —
    // lê-lo aqui seria o campo de outra entidade.
    partnerId = data.userPartner;
  } catch {
    // Sem o usuário não há cascata — mantém o detectado.
    ultimaOrigem = 'detected';
    return { code: null, source: 'detected' };
  }

  // Proprietário. `!= null` de propósito: o proprietário ZERO é válido.
  if (tenantId !== null && tenantId !== undefined && tenantId !== '') {
    try {
      const resp = await api.get(`/tenant/${tenantId}`);
      const doTenant = normalizarIdioma(lerCampo(resp, 'tenant', 'tenantLanguage'));
      if (doTenant) {
        ultimaOrigem = 'tenant';
        return { code: doTenant, source: 'tenant' };
      }
    } catch {
      /* rota ou campo ainda não existe — segue para o parceiro */
    }
  }

  // Parceiro. Idem quanto ao zero, que é o grupo raiz.
  if (partnerId !== null && partnerId !== undefined && partnerId !== '') {
    try {
      const resp = await api.get(`/partner/${partnerId}`);
      const doPartner = normalizarIdioma(
        lerCampo(resp, 'partner', 'partnerLanguage'),
      );
      if (doPartner) {
        ultimaOrigem = 'partner';
        return { code: doPartner, source: 'partner' };
      }
    } catch {
      /* rota ainda não existe — cai no detectado */
    }
  }

  ultimaOrigem = 'detected';
  return { code: null, source: 'detected' };
}

/**
 * Aplica o resultado da cascata.
 *
 * Só troca o idioma quando algum nível definiu um. Nada definido = mantém o
 * que o detector já escolheu, em vez de forçar um padrão.
 */
export async function aplicarIdiomaResolvido(r: IdiomaResolvido): Promise<void> {
  if (!r.code) return;
  if (i18n.language === r.code) return;
  await i18n.changeLanguage(r.code);
}

/** Chave onde o i18next guarda a preferência de idioma do aparelho. */
const CHAVE_IDIOMA = 'i18nextLng';

/** Há preferência explícita guardada, ou estamos seguindo o aparelho? */
export function seguindoOSistema(): boolean {
  try {
    return !localStorage.getItem(CHAVE_IDIOMA);
  } catch {
    return true;
  }
}

/**
 * Volta a seguir o idioma do APARELHO: esquece a preferência guardada.
 *
 * A ordem importa. `changeLanguage` regrava a chave (o detector tem
 * `caches: ['localStorage']`), então o apagamento tem de vir DEPOIS — senão
 * "esquecer" não esquece nada.
 */
export async function seguirPadraoDoSistema(): Promise<void> {
  const doAparelho = normalizarIdioma(navigator.language) ?? 'pt';
  ultimaOrigem = 'detected';
  await i18n.changeLanguage(doAparelho);
  try {
    localStorage.removeItem(CHAVE_IDIOMA);
  } catch {
    /* armazenamento bloqueado — segue só nesta sessão */
  }
}

/**
 * Esquece o idioma da SESSÃO que terminou.
 *
 * Sem isto o idioma do usuário anterior vira preferência do aparelho: quem
 * sair de uma conta em pt-PT e entrar noutra em pt-BR continua vendo pt-PT,
 * porque a chave guardada vence o detector. O idioma de uma sessão não pode
 * sobreviver a ela.
 */
export function esquecerIdiomaDaSessao(): void {
  try {
    localStorage.removeItem(CHAVE_IDIOMA);
  } catch {
    /* armazenamento bloqueado */
  }
  ultimaOrigem = 'detected';
}

/**
 * Grava a escolha do usuário: PUT /user/:id { userLanguage }.
 *
 * Fire-and-forget, como o `persistUserTheme` — a troca já aconteceu na tela, e
 * falhar ao gravar não deve desfazê-la.
 */
export function persistirIdiomaDoUsuario(code: string | null): void {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (!u?.userId) return;
    // NULO = "sem idioma próprio": o usuário volta a herdar do haras e do
    // parceiro. Por isso a origem deixa de ser 'user'.
    ultimaOrigem = code === null ? 'detected' : 'user';
    api
      .put(`/user/${u.userId}`, { userLanguage: code })
      .catch((e) => console.error('Erro ao salvar idioma do usuário:', e));
  } catch {
    /* sem usuário logado — a escolha fica só local */
  }
}
